import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

const FALLBACK = {
  hero_image: "https://static.prod-images.emergentagent.com/jobs/5e3ae1e5-d7f0-4d53-bd07-8508dddfbaaf/images/fb68053248c523776b8da6c09913c8984146a7f42e0678a8c1f06d09ee5d15bc.jpeg",
  brand_name: "Ventedaily", brand_tagline: "Enterprise Resource Planning",
  hero_title: "Semua proses bisnis, dalam satu kendali.",
  hero_subtitle: "Kelola produksi, persediaan, penjualan, dan operasional Ventedaily secara terintegrasi.",
  hero_quote: "Temani Setiap Momen Kebersamaan",
  welcome_title: "Selamat Datang", welcome_subtitle: "Masuk ke Ventedaily ERP",
  welcome_hint: "Gunakan akun perusahaan Anda untuk melanjutkan.",
  button_text: "Masuk ke ERP", footer_text: "© 2026 PT Vente Global Sejahtera • Internal Use Only",
};

const Monogram = ({ className }) => (
  <div className={className}>
    <svg viewBox="0 0 48 40" fill="none" className="w-full h-full">
      <path d="M4 4 L18 36 L24 22" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M44 4 L30 36 L24 22" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const nextUrl = (() => {
    const n = new URLSearchParams(window.location.search).get("next");
    return n && n.startsWith("/") ? n : "/";
  })();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(FALLBACK);
  const logoUrl = cfg.logo_url || "";

  useEffect(() => {
    api.get("/settings/login").then((r) => setCfg({ ...FALLBACK, ...r.data })).catch(() => {});
  }, []);
  if (user) {
    if (nextUrl.startsWith("/oidc/authorize")) { window.location.replace(nextUrl); return null; }
    return <Navigate to={nextUrl} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Berhasil masuk");
      if (nextUrl.startsWith("/oidc/authorize")) window.location.replace(nextUrl);
      else navigate(nextUrl);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal masuk");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex bg-[#f3ede4]">
      {/* Left hero */}
      <div className="hidden lg:block relative w-[46%] overflow-hidden">
        <img src={cfg.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="login-hero-image" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2b2118]/85 via-[#2b2118]/25 to-[#2b2118]/40" />
        <div className="relative h-full flex flex-col justify-between p-10 text-ivory">
          <div className="flex items-center gap-3">
            {logoUrl ? <img src={logoUrl} alt={cfg.brand_name} className="w-9 h-9 object-contain" data-testid="login-hero-logo" /> : <Monogram className="w-9 h-9 text-ivory" />}
            <div>
              <div className="font-heading font-bold text-2xl leading-none">{cfg.brand_name}</div>
              <div className="text-[11px] tracking-wide text-ivory/70 uppercase mt-1">{cfg.brand_tagline}</div>
            </div>
          </div>
          <div>
            <h1 className="font-heading font-extrabold text-4xl xl:text-5xl leading-[1.1]" data-testid="login-hero-title">{cfg.hero_title}</h1>
            <p className="mt-5 text-ivory/80 max-w-md text-base">{cfg.hero_subtitle}</p>
            <div className="mt-8 flex items-center gap-3 text-ivory/70 italic">
              <span className="w-8 h-px bg-ivory/40" />{cfg.hero_quote}
            </div>
          </div>
          <div className="text-ivory/50 text-xs">{cfg.footer_text}</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md bg-[#fbf7f1] border border-[#e6dccb] rounded-3xl shadow-xl p-8 sm:p-10">
          <div className="flex flex-col items-center text-center mb-7">
            {logoUrl ? <img src={logoUrl} alt={cfg.brand_name} className="w-12 h-12 object-contain mb-3" data-testid="login-card-logo" /> : <Monogram className="w-12 h-12 text-cocoa mb-3" />}
            <h2 className="font-heading font-extrabold text-3xl text-cocoa" data-testid="login-welcome-title">{cfg.welcome_title}</h2>
            <div className="text-taupe font-semibold mt-1">{cfg.welcome_subtitle}</div>
            <p className="text-wmuted text-sm mt-2">{cfg.welcome_hint}</p>
          </div>

          <form onSubmit={submit} data-testid="login-form" className="space-y-4">
            <div>
              <Label className="text-cocoa font-semibold">Email atau Username</Label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-taupe" />
                <Input data-testid="login-email" type="text" required value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="email atau username"
                  className="pl-10 bg-white h-11 rounded-xl" />
              </div>
            </div>
            <div>
              <Label className="text-cocoa font-semibold">Kata Sandi</Label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-taupe" />
                <Input data-testid="login-password" type={show ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)} className="pl-10 pr-10 bg-white h-11 rounded-xl" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-taupe" data-testid="login-toggle-password">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer text-cocoa">
                <Checkbox checked={remember} onCheckedChange={setRemember} data-testid="login-remember" /> Ingat saya
              </label>
              <span className="text-taupe font-medium cursor-pointer hover:underline">Lupa kata sandi?</span>
            </div>

            <Button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full h-12 rounded-xl bg-cocoa hover:bg-cocoa-dark text-ivory text-base font-semibold">
              {busy ? "Memproses..." : <>{cfg.button_text} <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5 text-xs text-wmuted">
            <span className="flex-1 h-px bg-[#e6dccb]" /> atau <span className="flex-1 h-px bg-[#e6dccb]" />
          </div>
          <Button variant="outline" disabled className="w-full h-12 rounded-xl border-[#d9ccb8] text-cocoa">
            <ShieldCheck className="w-4 h-4 mr-2" /> Masuk dengan Akun Perusahaan
          </Button>
          <div className="flex items-center justify-center gap-1.5 text-xs text-wmuted mt-5">
            <Lock className="w-3.5 h-3.5" /> Akses aman dan aktivitas akun tercatat
          </div>
        </div>
        <div className="text-center text-sm text-wmuted mt-6">
          Mengalami kendala akses?<br /><span className="text-cocoa font-medium underline">Hubungi Administrator Sistem</span>
        </div>
      </div>
    </div>
  );
}
